import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { TrendingUp } from 'lucide-react'
import { format, isValid, parseISO } from 'date-fns'
import { Skeleton } from '../common/LoadingSkeleton'
import { TREND_CHART_RANGE_OPTIONS } from '../../utils/analyticsTrendScores'

const SVG_WIDTH = 960
const SVG_HEIGHT = 320
const CHART_MARGIN = { top: 18, right: 24, bottom: 56, left: 58 }
const PLOT_WIDTH = SVG_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right
const PLOT_HEIGHT = SVG_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom
const DEFAULT_Y_TICKS = [0, 25, 50, 75, 100]
const DEFAULT_SCORE_BOUNDS = { min: 0, max: 100 }

const defaultValueFormatter = (value) => {
  if (!Number.isFinite(value)) return '0'
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10)
}

const niceNumber = (range, round) => {
  if (!Number.isFinite(range) || range <= 0) return 10

  const exponent = Math.floor(Math.log10(range))
  const fraction = range / 10 ** exponent
  let niceFraction

  if (round) {
    if (fraction < 1.5) niceFraction = 1
    else if (fraction < 3) niceFraction = 2
    else if (fraction < 7) niceFraction = 5
    else niceFraction = 10
  } else if (fraction <= 1) niceFraction = 1
  else if (fraction <= 2) niceFraction = 2
  else if (fraction <= 5) niceFraction = 5
  else niceFraction = 10

  return niceFraction * 10 ** exponent
}

const stepPrecision = (step) => {
  if (!Number.isFinite(step) || step >= 1) return 0
  return Math.min(2, Math.ceil(-Math.log10(step)))
}

const roundForStep = (value, step) => {
  const precision = stepPrecision(step)
  return Number(value.toFixed(precision))
}

const buildDynamicYAxis = (scores, bounds = DEFAULT_SCORE_BOUNDS) => {
  const hasMinBound = Number.isFinite(bounds?.min)
  const hasMaxBound = Number.isFinite(bounds?.max)
  const defaultMin = hasMinBound ? bounds.min : 0
  const defaultMax = hasMaxBound ? bounds.max : 100

  if (scores.length === 0) {
    return { min: defaultMin, max: defaultMax, ticks: DEFAULT_Y_TICKS }
  }

  let dataMin = Math.min(...scores)
  let dataMax = Math.max(...scores)

  if (dataMin === dataMax) {
    const padding =
      hasMinBound && hasMaxBound
        ? Math.max(2, (bounds.max - bounds.min) * 0.1)
        : Math.max(2, Math.abs(dataMin) * 0.12 || 5)
    dataMin -= padding
    dataMax += padding
  } else {
    const padding = Math.max(2, (dataMax - dataMin) * 0.18)
    dataMin -= padding
    dataMax += padding
  }

  if (hasMinBound) dataMin = Math.max(bounds.min, dataMin)
  if (hasMaxBound) dataMax = Math.min(bounds.max, dataMax)

  const step = Math.max(0.5, niceNumber((dataMax - dataMin) / 4, true))
  let min = Math.floor(dataMin / step) * step
  let max = Math.ceil(dataMax / step) * step

  if (hasMinBound) min = Math.max(bounds.min, min)
  if (hasMaxBound) max = Math.min(bounds.max, max)

  if (max <= min) {
    max = hasMaxBound ? Math.min(bounds.max, min + step) : min + step
  }

  const ticks = []
  for (let value = min; value <= max + step * 0.5; value += step) {
    ticks.push(roundForStep(value, step))
  }

  if (ticks.length < 3) {
    return {
      min,
      max,
      ticks: [
        roundForStep(min, step),
        roundForStep((min + max) / 2, step),
        roundForStep(max, step),
      ],
    }
  }

  return { min, max, ticks }
}

const buildTickIndices = (pointsLength) => {
  if (pointsLength <= 1) return [0]

  const maxTickCount = Math.min(pointsLength, 6)
  const indices = new Set([0, pointsLength - 1])

  if (maxTickCount > 2) {
    const lastIndex = pointsLength - 1
    const step = lastIndex / (maxTickCount - 1)

    for (let tick = 1; tick < maxTickCount - 1; tick += 1) {
      indices.add(Math.round(step * tick))
    }
  }

  return [...indices].sort((left, right) => left - right)
}

const buildAreaPoints = (coordinates, plotBottom) => {
  if (coordinates.length < 2) return null

  const first = coordinates[0]
  const last = coordinates[coordinates.length - 1]

  return [
    `${first.x},${plotBottom}`,
    ...coordinates.map((point) => `${point.x},${point.y}`),
    `${last.x},${plotBottom}`,
  ].join(' ')
}

/** Dashboard `/meeting/:id` target when the series provides `navigateMeetingId` on the point. */
function navigateMeetingIdFromTrendPoint(point) {
  const explicit =
    typeof point?.navigateMeetingId === 'string' ? point.navigateMeetingId.trim() : ''
  return explicit || null
}

/** Calendar label for hover tooltip (no native SVG <title>). */
function tooltipDateLabelForPoint(point) {
  const explicit =
    typeof point?.tooltipDateLabel === 'string' ? point.tooltipDateLabel.trim() : ''
  if (explicit) return explicit

  const id = point?.id
  if (typeof id === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(id)) {
    const d = parseISO(id)
    return isValid(d) ? format(d, 'MMM d, yyyy') : ''
  }

  const lbl = typeof point?.label === 'string' ? point.label.trim() : ''
  return lbl
}

export default function TrendChartCard({
  icon: Icon = TrendingUp,
  title,
  description,
  range,
  onRangeChange,
  series,
  loading,
  ariaLabel,
  emptyMessage = 'No meeting data for this range.',
  transitionDelay = 0,
  showRangeSelector = true,
  statusLabel,
  valueFormatter = defaultValueFormatter,
  yAxisTickFormatter = defaultValueFormatter,
  yAxisBounds = DEFAULT_SCORE_BOUNDS,
  /** When set, dots with a resolvable meeting id navigate via this callback (see Meeting History links). */
  onTrendMeetingNavigate,
}) {
  const [hoveredPoint, setHoveredPoint] = useState(null)
  const [chartWidth, setChartWidth] = useState(SVG_WIDTH)
  const chartFrameRef = useRef(null)
  const gradientId = `trendFill${useId().replace(/:/g, '')}`
  const clipPathId = `trendClip${useId().replace(/:/g, '')}`
  const points = useMemo(() => series?.points ?? [], [series?.points])
  const svgWidth = chartWidth
  const plotWidth = Math.max(1, svgWidth - CHART_MARGIN.left - CHART_MARGIN.right)
  const plotHeight = SVG_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom

  useEffect(() => {
    const node = chartFrameRef.current
    if (!node) return undefined

    const updateWidth = (nextWidth) => {
      const normalizedWidth = Math.max(320, Math.round(nextWidth))
      setChartWidth((currentWidth) => (currentWidth === normalizedWidth ? currentWidth : normalizedWidth))
    }

    updateWidth(node.getBoundingClientRect().width)

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        updateWidth(entry.contentRect.width)
      })
      observer.observe(node)
      return () => observer.disconnect()
    }

    const handleResize = () => updateWidth(node.getBoundingClientRect().width)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const chartGeometry = useMemo(() => {
    const scores = points.map((point) => point.score).filter((value) => Number.isFinite(value))
    const yAxis = buildDynamicYAxis(scores, yAxisBounds)
    const xStep = points.length > 1 ? plotWidth / (points.length - 1) : 0
    const plotBottom = CHART_MARGIN.top + plotHeight

    const coordinates = points.map((point, index) => {
      const x = points.length === 1 ? CHART_MARGIN.left + plotWidth / 2 : CHART_MARGIN.left + index * xStep
      const ratio = (point.score - yAxis.min) / Math.max(1, yAxis.max - yAxis.min)
      const y = CHART_MARGIN.top + (1 - ratio) * plotHeight
      return { x, y }
    })

    return {
      yAxis,
      plotBottom,
      coordinates,
      linePoints: coordinates.map((point) => `${point.x},${point.y}`).join(' '),
      areaPoints: buildAreaPoints(coordinates, plotBottom),
      xTickIndices: buildTickIndices(points.length),
    }
  }, [plotHeight, plotWidth, points, yAxisBounds])

  const { yAxis, plotBottom, coordinates, linePoints, areaPoints, xTickIndices } = chartGeometry
  const shouldShowRangeSelector = showRangeSelector && range && typeof onRangeChange === 'function'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: transitionDelay }}
      className="glass p-6"
    >
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-gray-900 mb-1">
            <Icon className="w-5 h-5 text-primary-600" />
            <h2 className="text-xl font-semibold">{title}</h2>
          </div>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <div className="flex flex-col items-start lg:items-end gap-2">
          {shouldShowRangeSelector && (
            <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1">
              {TREND_CHART_RANGE_OPTIONS.map((option) => {
                const isActive = range === option.id
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => onRangeChange(option.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>
          )}
          <p className="text-xs text-gray-400">{statusLabel ?? `Showing ${series?.label ?? 'No data'}`}</p>
        </div>
      </div>

      <div
        ref={chartFrameRef}
        className="bg-white/60 rounded-2xl p-4 border border-gray-100 overflow-hidden"
      >
        {loading ? (
          <Skeleton className="h-72 w-full rounded-2xl" />
        ) : points.length > 0 ? (
          <svg
            viewBox={`0 0 ${svgWidth} ${SVG_HEIGHT}`}
            className="w-full h-72"
            role="img"
            aria-label={ariaLabel ?? `${title} chart`}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#6366f1" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0.02" />
              </linearGradient>
              <clipPath id={clipPathId}>
                <rect x={CHART_MARGIN.left} y={CHART_MARGIN.top} width={plotWidth} height={plotHeight} rx="14" />
              </clipPath>
            </defs>

            {yAxis.ticks.map((tick) => {
              const ratio = (tick - yAxis.min) / Math.max(1, yAxis.max - yAxis.min)
              const y = CHART_MARGIN.top + (1 - ratio) * plotHeight
              return (
                <g key={`y-tick-${tick}`}>
                  <line
                    x1={CHART_MARGIN.left}
                    x2={svgWidth - CHART_MARGIN.right}
                    y1={y}
                    y2={y}
                    stroke="#e5e7eb"
                    strokeDasharray="4 6"
                  />
                  <text
                    x={CHART_MARGIN.left - 10}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="12"
                    fill="#94a3b8"
                  >
                    {yAxisTickFormatter(tick)}
                  </text>
                </g>
              )
            })}

            <line
              x1={CHART_MARGIN.left}
              x2={svgWidth - CHART_MARGIN.right}
              y1={plotBottom}
              y2={plotBottom}
              stroke="#dbe3ef"
            />

            <g clipPath={`url(#${clipPathId})`}>
              {areaPoints && <polygon points={areaPoints} fill={`url(#${gradientId})`} />}
              {coordinates.length > 1 && (
                <polyline
                  points={linePoints}
                  fill="none"
                  stroke="#6366f1"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </g>

            {coordinates.map((point, index) => {
              const pt = points[index]
              const navigateMeetingId = navigateMeetingIdFromTrendPoint(pt)
              const canNavigate =
                typeof onTrendMeetingNavigate === 'function' && navigateMeetingId != null
              const dateStr = tooltipDateLabelForPoint(pt)
              const scoreStr = valueFormatter(pt.score)
              const lineDate = dateStr ? `Date: ${dateStr}` : null
              const lineScore = `Score: ${scoreStr}`
              const lines = lineDate ? [lineDate, lineScore] : [lineScore]
              const longest = lines.reduce((m, l) => Math.max(m, l.length), 0)
              const tooltipWidth = Math.min(
                svgWidth - 16,
                Math.max(120, longest * 6.8 + 28),
              )
              const tooltipHeight = lines.length === 2 ? 46 : 26

              return (
                <g
                  key={pt.id ?? `${pt.label}-${pt.score}-${index}`}
                  onMouseEnter={() => setHoveredPoint(index)}
                  onMouseLeave={() => setHoveredPoint(null)}
                  style={{ cursor: canNavigate ? 'pointer' : 'default' }}
                  onClick={(e) => {
                    if (!canNavigate || navigateMeetingId == null) return
                    e.preventDefault()
                    e.stopPropagation()
                    onTrendMeetingNavigate(navigateMeetingId)
                  }}
                >
                  <circle cx={point.x} cy={point.y} r="14" fill="transparent" />
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={hoveredPoint === index ? 6 : 4}
                    fill={hoveredPoint === index ? '#6366f1' : '#ffffff'}
                    stroke="#6366f1"
                    strokeWidth="2"
                    className="transition-all duration-150"
                  />
                  {hoveredPoint === index && (() => {
                    const tooltipAbove = point.y > CHART_MARGIN.top + tooltipHeight + 28
                    const tooltipY = tooltipAbove ? point.y - tooltipHeight - 12 : point.y + 14
                    const textStartY =
                      tooltipY + (lines.length === 2 ? 17 : tooltipHeight / 2 + 4)
                    const anchorX =
                      Math.min(
                        svgWidth - CHART_MARGIN.right - tooltipWidth / 2 - 4,
                        Math.max(CHART_MARGIN.left + tooltipWidth / 2 + 4, point.x),
                      )
                    return (
                      <>
                        <rect
                          x={anchorX - tooltipWidth / 2}
                          y={tooltipY}
                          width={tooltipWidth}
                          height={tooltipHeight}
                          rx="8"
                          fill="#1f2937"
                        />
                        <text
                          x={anchorX}
                          y={textStartY}
                          textAnchor="middle"
                          fill="#f9fafb"
                          fontSize="11"
                          fontWeight="600"
                        >
                          {lines.map((line, li) => (
                            <tspan key={li} x={anchorX} dy={li === 0 ? 0 : 15}>
                              {line}
                            </tspan>
                          ))}
                        </text>
                      </>
                    )
                  })()}
                </g>
              )
            })}

            {xTickIndices.map((index, tickIndex) => {
              const point = points[index]
              const coordinate = coordinates[index]
              if (!point || !coordinate) return null

              const isFirst = tickIndex === 0
              const isLast = tickIndex === xTickIndices.length - 1
              const textAnchor =
                xTickIndices.length === 1 ? 'middle' : isFirst ? 'start' : isLast ? 'end' : 'middle'

              return (
                <g key={`x-tick-${point.id ?? `${point.label}-${index}`}`}>
                  <line
                    x1={coordinate.x}
                    x2={coordinate.x}
                    y1={plotBottom}
                    y2={plotBottom + 6}
                    stroke="#cbd5e1"
                  />
                  <text
                    x={coordinate.x}
                    y={plotBottom + 22}
                    textAnchor={textAnchor}
                    fontSize="12"
                    fill="#94a3b8"
                  >
                    {point.label}
                  </text>
                </g>
              )
            })}
          </svg>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <p>{emptyMessage}</p>
          </div>
        )}
      </div>
    </motion.div>
  )
}
